import { Link, useLocation } from "@tanstack/react-router"
import { ExternalLinkNotice } from "@/components/external-link-notice"
import { KitchenHoursNotice } from "@/components/kitchen-hours-notice"
import {
  BAKERY_PHONE,
  BAKERY_PHONE_TEL,
  CATERING_EMAIL,
  TOAST_ORDER_URL,
} from "@/lib/constants/site"
import { getToastMenuImage } from "@/lib/data/toast-menu-images"
import {
  findToastItem,
  type LandingItem,
  type LandingLinkTarget,
  type LandingPage,
  type LandingSection,
} from "@/lib/data/landing/types"
import { useCateringVisible } from "@/lib/hooks/use-site-settings"
import { trackOrderClick } from "@/lib/utils/analytics"
import { getCountryCodeFromPath } from "@/lib/utils/region"

const LINK_ROUTES = {
  about: "/$countryCode/about",
  brunch: "/$countryCode/brunch",
  coffee: "/$countryCode/coffee",
  "salt-bread": "/$countryCode/salt-bread",
  matcha: "/$countryCode/matcha",
  menu: "/$countryCode/menu",
  visit: "/$countryCode/visit",
} as const satisfies Record<LandingLinkTarget, string>

const TEXT_LINK_CLASS =
  "inline-flex font-body text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-coral-text)] transition-colors hover:text-[#5F5156]"

const formatPrice = (price: number) => `$${price.toFixed(2)}`

// Rendered width of an item card: one column on phones, two from sm, three from
// lg inside the max-w-5xl content column (976px of content, 20px gaps).
const ITEM_CARD_IMAGE_SIZES =
  "(min-width: 1024px) 312px, (min-width: 640px) calc((100vw - 68px) / 2), calc(100vw - 40px)"

function ItemCard({ item }: { item: LandingItem }) {
  const toast = findToastItem(item.name)
  const label = item.label ?? item.name
  const description = item.note ?? toast?.description
  // A hand-picked photo (item.image) wins over the synced Toast one.
  const toastImage = item.image ? undefined : getToastMenuImage(item.name)
  const image = item.image ?? toastImage?.src

  return (
    <li className="flex flex-col overflow-hidden rounded-2xl border border-[#FBD1C8]/40 bg-white">
      {image && (
        <div className="aspect-square overflow-hidden bg-[#F2F0EF]">
          <img
            src={image}
            srcSet={toastImage?.srcSet}
            sizes={toastImage ? ITEM_CARD_IMAGE_SIZES : undefined}
            alt={item.imageAlt ?? `${label} at +Bake Cafe`}
            loading="lazy"
            decoding="async"
            width={toastImage?.width ?? 800}
            height={toastImage?.height ?? 800}
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col px-5 py-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-body text-[15px] font-semibold leading-snug text-[#5F5156]">
            {label}
          </h3>
          {toast && (
            <span className="shrink-0 font-body text-sm font-semibold text-[#5F5156]">
              {formatPrice(toast.price)}
            </span>
          )}
        </div>
        {description && (
          <p className="mt-1 font-body text-sm leading-relaxed text-[#5D5054]">
            {description}
          </p>
        )}
      </div>
    </li>
  )
}

function CateringContact({
  label,
  countryCode,
}: {
  label: string
  countryCode: string
}) {
  const cateringVisible = useCateringVisible()

  return (
    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
      <a href={`mailto:${CATERING_EMAIL}`} className="btn-coral text-center text-sm">
        Email {CATERING_EMAIL}
      </a>
      <a href={`tel:${BAKERY_PHONE_TEL}`} className="btn-outline text-center text-sm">
        Call {BAKERY_PHONE}
      </a>
      {cateringVisible && (
        <Link
          to="/$countryCode/catering"
          params={{ countryCode }}
          className={`${TEXT_LINK_CLASS} items-center justify-center`}
        >
          {label} →
        </Link>
      )}
    </div>
  )
}

function Section({
  section,
  countryCode,
}: {
  section: LandingSection
  countryCode: string
}) {
  const headingId = `${section.id}-heading`

  return (
    <section id={section.id} aria-labelledby={headingId} className="mb-16 scroll-mt-28">
      <div className="mb-6 flex items-center gap-4 border-b border-[#FBD1C8]/60 pb-4">
        <h2 id={headingId} className="font-display text-2xl text-[#5F5156]">
          {section.heading}
        </h2>
        <div className="h-px flex-1 bg-[#FBD1C8]/40" />
      </div>
      <div className="space-y-4 font-body text-sm leading-relaxed text-[#5D5054] md:text-base">
        {section.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
      {section.items && section.items.length > 0 && (
        <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {section.items.map((item) => (
            <ItemCard key={item.name} item={item} />
          ))}
        </ul>
      )}
      {section.cateringLink && (
        <CateringContact label={section.cateringLink} countryCode={countryCode} />
      )}
      {section.links && section.links.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
          {section.links.map((link) => (
            <Link
              key={link.to}
              to={LINK_ROUTES[link.to]}
              params={{ countryCode }}
              className={TEXT_LINK_CLASS}
            >
              {link.label} →
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

export default function LandingPageView({ page }: { page: LandingPage }) {
  const location = useLocation()
  const countryCode = getCountryCodeFromPath(location.pathname) || "us"

  return (
    <div className="min-h-screen bg-[#FFF9F8]">
      <header className="border-b border-[#FBD1C8]/40 bg-[#FCEAE7]">
        <div className="content-container grid gap-10 py-14 md:py-20 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <p className="section-eyebrow mb-3">{page.eyebrow}</p>
            <h1 className="section-title mb-5 text-4xl md:text-5xl">{page.h1}</h1>
            <p className="max-w-2xl font-body text-base leading-relaxed text-[#5D5054] md:text-lg">
              {page.intro}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={TOAST_ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOrderClick("order_online_click", page.trackingLocation)}
                className="btn-coral text-center text-sm"
              >
                Order Pickup on Toast
                <ExternalLinkNotice />
              </a>
              <Link
                to="/$countryCode/visit"
                params={{ countryCode }}
                className="btn-outline text-center text-sm"
              >
                Hours & Directions
              </Link>
            </div>
            <KitchenHoursNotice className="mt-6" />
          </div>
          <div className="overflow-hidden rounded-3xl bg-[#FBD1C8]/25">
            <img
              src={page.hero.src}
              srcSet={page.hero.srcSet}
              sizes="(min-width: 1024px) 40vw, 100vw"
              alt={page.hero.alt}
              width={page.hero.width}
              height={page.hero.height}
              loading="eager"
              decoding="async"
              className="aspect-[4/5] max-h-[560px] w-full object-cover"
            />
          </div>
        </div>
      </header>

      <div className="content-container mx-auto max-w-5xl py-16">
        {page.sections.map((section) => (
          <Section key={section.id} section={section} countryCode={countryCode} />
        ))}

        <section id="faq" aria-labelledby="faq-heading" className="mb-4 scroll-mt-28">
          <div className="mb-6 flex items-center gap-4 border-b border-[#FBD1C8]/60 pb-4">
            <h2 id="faq-heading" className="font-display text-2xl text-[#5F5156]">
              Questions
            </h2>
            <div className="h-px flex-1 bg-[#FBD1C8]/40" />
          </div>
          <dl className="divide-y divide-[#FBD1C8]/60">
            {page.faq.map((entry) => (
              <div key={entry.q} className="py-5 first:pt-0">
                <dt className="font-body text-[15px] font-semibold text-[#5F5156]">
                  {entry.q}
                </dt>
                <dd className="mt-2 font-body text-sm leading-relaxed text-[#5D5054] md:text-base">
                  {entry.a}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </div>
  )
}
